import React from 'react';

import PropTypes from 'prop-types';

import {
    Avatar,
    CleanIcon,
    Grid,
    GridItem,
    ListAdornment,
    ListBody,
    ListItem,
    MaskIcon,
    OnTimeIcon,
    SeatsIcon,
    Section,
    StaffIcon,
    Typography,
    ValueIcon,
    WifiIcon,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import cgConfig from '../../../../config/carrierguide.locale';

import styles from './CarrierBest.scss';

const FEATURE_MAP = {
    covidCount: {
        icon: MaskIcon,
    },
    wifiCount: {
        icon: WifiIcon,
    },
    staffCount: {
        icon: StaffIcon,
    },
    valueCount: {
        icon: ValueIcon,
    },
    ontimeCount: {
        icon: OnTimeIcon,
    },
    comfortCount: {
        icon: SeatsIcon,
    },
    vehicleCount: {
        icon: CleanIcon,
    },
};

function TableBody(props) {
    return (
        <table className={styles.table}>
            <tbody {...props} />
        </table>
    );
}

function FeaturesTable(props) {
    const {
        t,
        title,
        features,
        className,
    } = props;

    return (
        <>
            <Typography
                className={`${styles.subtitle} ${className}`}
                component="h3"
                variant="subtitle"
            >
                {title}
            </Typography>
            <Grid
                columnSpacing={6}
                columns={2}
                component={TableBody}
                rowSpacing={0}
            >
                {features.map((feature, idx) => {
                    const Icon = FEATURE_MAP[feature.key].icon;

                    return (
                        <GridItem
                            component={ListItem}
                            componentProps={{ component: 'tr' }}
                            key={idx}
                            sm={1}
                            xs={2}
                        >
                            <ListAdornment
                                component="td"
                                aria-hidden
                            >
                                <Avatar variant="square">
                                    <Icon />
                                </Avatar>
                            </ListAdornment>
                            <ListBody
                                component="td"
                            >
                                <Grid
                                    columns={1}
                                    component={TableBody}
                                    rowSpacing={0}
                                >
                                    <GridItem
                                        align="start"
                                        component={ListItem}
                                        componentProps={{
                                            component: 'tr',
                                            spacing: 0,
                                        }}
                                        sm={1}
                                        xs={2}
                                    >
                                        <ListBody
                                            component={Typography}
                                            componentProps={{ component: 'td' }}
                                            variant="h6"
                                        >
                                            {t(feature.label)}
                                        </ListBody>
                                    </GridItem>
                                    <GridItem
                                        align="start"
                                        component={ListItem}
                                        componentProps={{
                                            component: 'tr',
                                            spacing: 0,
                                        }}
                                        sm={1}
                                        xs={2}
                                    >
                                        <ListAdornment
                                            component={Typography}
                                            componentProps={{ component: 'td' }}
                                            justify="start"
                                        >
                                            {t('bestBasedOn', { bestReviewCount: feature.count })}
                                        </ListAdornment>
                                    </GridItem>
                                </Grid>
                            </ListBody>
                        </GridItem>
                    );
                })}
            </Grid>
        </>
    );
}

const LocalizedFeaturesTable = localize(FeaturesTable, cgConfig.namespace);

function CarrierBest(props) {
    const {
        t,
        features,
    } = props;
    const {
        covidFeatures = [],
        generalFeatures = [],
    } = features;

    if (!covidFeatures.length && !generalFeatures.length) {
        return null;
    }

    return (
        <Section id="carrier-best-section">
            <Typography
                className={styles.heading}
                variant="h3"
            >
                {t('bestTitle')}
            </Typography>
            <Typography
                className={styles.intro}
                paragraph
            >
                {t('bestIntro')}
            </Typography>
            {!!covidFeatures.length && (
                <LocalizedFeaturesTable
                    features={covidFeatures}
                    title={t('bestCovidTitle')}
                />
            )}
            {!!generalFeatures.length > 0 && (
                <LocalizedFeaturesTable
                    className={styles.generalFeatures}
                    features={generalFeatures}
                    title={t('bestGeneralTitle')}
                />
            )}
        </Section>
    );
}

CarrierBest.propTypes = {
    features: PropTypes.shape({
        covidFeatures: PropTypes.arrayOf(PropTypes.object),
        generalFeatures: PropTypes.arrayOf(PropTypes.object),
    }),
};
CarrierBest.defaultProps = {
    features: {},
};

export {
    CarrierBest,
    FeaturesTable,
};
export default localize(CarrierBest, cgConfig.namespace);
